import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useAgentStore from '@/features/Agent/stores';
import useProductStore from '@/features/Product/store/ProductStore';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useLocaleStore from '@/stores/LocaleStore';
import useCommonStore from '@/stores/pure/CommonStore';
import { useToast } from '@/stores/pure/ToastStore';
import useUIStore from '@/stores/pure/UIStore';
import useTagStore from '@/stores/TagStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import useUserRole from './useUserRole';
import useWorkspaceId from './useWorkspaceId';

const useAuth = () => {
  const { navigate } = useWorkspaceRouter();
  const addToast = useToast();
  const location = useLocation();
  const wa = useWorkspaceId();
  const [verified, setVerified] = useState(true);
  const { setUser, user } = useUserStore((state) => ({
    setUser: state.setUser,
    user: state.user,
  }));
  const {
    currWorkspace,
    workspaces,
    fetchWorkspace,
    fetchWorkspaces,
    fetchWorkspaceMembers,
  } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
    workspaces: state.workspaces,
    fetchWorkspace: state.fetchWorkspace,
    fetchWorkspaces: state.fetchWorkspaces,
    fetchWorkspaceMembers: state.fetchWorkspaceMembers,
  }));
  const fetchRegion = useLocaleStore((state) => state.fetchRegionMap);
  const fetchLanguageList = useLocaleStore((state) => state.fetchLanguageList);
  const { isEditor } = useUserRole();
  const { appInit, appLoadingState, setState } = useCommonStore(
    ({ appInit, appLoadingState, setState }) => ({
      appInit,
      appLoadingState,
      setState,
    }),
  );
  const { setSidebarHidden } = useUIStore((state) => ({
    setSidebarHidden: state.setSidebarHidden,
  }));
  const fetchAndCreateTagMap = useTagStore(
    (state) => state.fetchAndCreateTagMap,
  );
  const fetchAgentList = useAgentStore((state) => state.fetchAgentList);
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );
  const fetchFullTaxonomy = useTaxonomyStore(
    (state) => state.fetchFullTaxonomy,
  );

  useEffect(() => {
    (async () => {
      const data = await service.auth.authCheck();
      if (data && data.workspaces?.length) {
        setUser({
          user_id: data.user_id,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          hasWorkspace: true,
          color: data.color,
        });
        setVerified(true);
      } else {
        setVerified(false);
        setUser(null);
        // Cookies are handled by server
        navigate('/');
        addToast('Your session has expired. Please login again.', 'fail');
      }
    })();
  }, [addToast, navigate, setUser]);

  useEffect(() => {
    (async () => {
      if (appLoadingState || !user?.user_id) return;
      const reroute = () => {
        navigate('/500', { replace: true });
      };
      const request = (alias: string) => {
        navigate(`/request-access/${alias}`, { replace: true });
      };
      const data = await service.auth.authCheck();
      if (!data) {
        window.location.reload();
        return;
      }
      if (user.hasWorkspace && workspaces.length === 0) {
        setState({
          appLoadingState: true,
        });
        await fetchWorkspaces();
        setState({
          appLoadingState: false,
        });
        return;
      }
      if (currWorkspace?.alias) return;
      if (appInit) return;
      if (user.hasWorkspace) {
        if (!workspaces.length) return;
        if (typeof wa === 'string') {
          setState({
            appLoadingState: true,
          });
          await fetchWorkspace(wa, () => request(wa));
          setState({
            appLoadingState: false,
          });
        } else {
          reroute();
        }
        return;
      } else if (typeof wa === 'string' && !verified) {
        navigate(`/login?from=${encodeURIComponent(location.pathname)}`, {
          replace: true,
        });
      }
    })();
  }, [
    appInit,
    appLoadingState,
    currWorkspace?.alias,
    fetchWorkspace,
    fetchWorkspaces,
    navigate,
    user?.user_id,
    verified,
    wa,
    location.pathname,
    workspaces.length,
    user,
    setState,
  ]);

  const initialize = useCallback(async () => {
    if (!appInit) {
      if (currWorkspace?.alias) {
        const fetchBaseData = async () => {
          await Promise.all([
            fetchWorkspaceMembers(),
            fetchAndCreateTagMap(currWorkspace.alias),
            fetchFeedbackSources(currWorkspace.alias),
            fetchProductList(currWorkspace.alias),
            fetchRegion(currWorkspace.alias),
            fetchLanguageList(currWorkspace.alias),
            fetchFullTaxonomy(currWorkspace.alias),
          ]);
          setSidebarHidden(false);
        };
        await fetchBaseData();
        setState({ appInit: true });
      }
    }
  }, [
    currWorkspace?.alias,
    appInit,
    setState,
    fetchWorkspaceMembers,
    fetchAndCreateTagMap,
    fetchFeedbackSources,
    fetchProductList,
    fetchRegion,
    fetchLanguageList,
    fetchFullTaxonomy,
    setSidebarHidden,
  ]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (isEditor) {
      fetchAgentList();
    }
  }, [isEditor, fetchAgentList]);

  return verified;
};

export default useAuth;
